import React from 'react';
import { useLocation } from 'react-router-dom';
import NavMenuLinks from '@/components/NavMenuLinks';
import { findActiveNavGroupId, groupHasActiveItem } from '@/components/navMatching';
import type { NavGroup } from '@/components/layoutNav';
import { useNavGroupAccordion } from '@/hooks/useNavGroupAccordion';

type NavGroupAccordionProps = {
  navGroups: NavGroup[];
  variant?: 'desktop' | 'mobile';
  linkClassName?: string;
  onNavigate?: () => void;
  iconOnly?: boolean;
  className?: string;
  'data-testid'?: string;
};

export default function NavGroupAccordion({
  navGroups,
  variant = 'desktop',
  linkClassName = 'nav-link',
  onNavigate,
  iconOnly = false,
  className,
  'data-testid': testId,
}: NavGroupAccordionProps) {
  const location = useLocation();
  const activeGroupId = findActiveNavGroupId(navGroups, location.pathname);
  const groupIds = navGroups.map((group) => group.id);
  const { isExpanded, toggleGroup } = useNavGroupAccordion(activeGroupId, groupIds);

  if (iconOnly) {
    const allItems = navGroups.flatMap((group) => group.items);
    return (
      <nav
        className={`nav-accordion nav-accordion--icon-rail${className ? ` ${className}` : ''}`}
        aria-label="Main navigation"
        data-testid={testId}
      >
        <NavMenuLinks
          items={allItems}
          linkClassName={`${linkClassName} nav-link--icon-only`}
          onNavigate={onNavigate}
          iconOnly
        />
      </nav>
    );
  }

  const navClassName = [
    'nav-accordion',
    variant === 'mobile' ? 'nav-accordion--mobile' : 'nav-accordion--desktop',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <nav className={navClassName} aria-label="Main navigation" data-testid={testId}>
      {navGroups.map((group) => {
        const expanded = isExpanded(group.id);
        const isActiveGroup = groupHasActiveItem(group, location.pathname);
        return (
          <div
            key={group.id}
            className={`nav-accordion-group${isActiveGroup ? ' nav-accordion-group--active' : ''}`}
            data-nav-group={group.id}
          >
            <button
              type="button"
              className="nav-accordion-toggle"
              aria-expanded={expanded}
              aria-controls={`nav-group-panel-${group.id}`}
              id={`nav-group-toggle-${group.id}`}
              onClick={() => toggleGroup(group.id)}
            >
              <span>{group.label}</span>
              <span
                className={`nav-accordion-chevron${expanded ? ' nav-accordion-chevron--expanded' : ''}`}
                aria-hidden
              >
                ▸
              </span>
            </button>
            {expanded ? (
              <div
                className="nav-accordion-items"
                id={`nav-group-panel-${group.id}`}
                role="region"
                aria-labelledby={`nav-group-toggle-${group.id}`}
              >
                <NavMenuLinks
                  items={group.items}
                  linkClassName={
                    variant === 'mobile' ? `${linkClassName} mobile-nav-link` : linkClassName
                  }
                  onNavigate={onNavigate}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}
